import { useAuth0 } from '@auth0/auth0-react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { customFetch } from '@/lib/utils';

import { PaginatedConversationsResponse } from '../types';

const PAGE_SIZE = 10;

async function fetchChatHistory(
  accessToken: string,
  page: number
): Promise<PaginatedConversationsResponse> {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(PAGE_SIZE)
  });

  const response = await customFetch(`/backend/conversations?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    let errorBody: unknown;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = { message: `Request failed with status ${response.status}` };
    }
    throw errorBody;
  }

  return response.json() as Promise<PaginatedConversationsResponse>;
}

export function useChatHistory() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  return useInfiniteQuery({
    queryKey: ['chat-history'],
    queryFn: async ({ pageParam }) => {
      const accessToken = await getAccessTokenSilently();
      return fetchChatHistory(accessToken, pageParam);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page = 1, total_pages = 0 } = lastPage.metadata ?? {};
      return page < total_pages ? page + 1 : undefined;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}
